import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Filter, Network, Period } from '@models/online-reputation.models';
import { FilterStore } from '@core/stores/filter.store';
import { Dispatcher } from '@ngrx/signals/events';
import { TranslateService } from '@ngx-translate/core';

import {
  applyFilter,
  PeriodFilterValue,
  PeriodPreset,
  toggleFilterDrawer
  ,
  clearAllFilters
} from '@core/events/filter.events';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/ui/button/button.component';

// Local AgentOption type used for UI (id sent to API, name shown in UI)
type AgentOption = {
  id: string;
  name: string;
};

type SupportedFilters = Pick<Network, 'markets' | 'brands' | 'dealers' | 'outlets' | 'agents'>;
type FilterKey = keyof SupportedFilters;
type PeriodType = PeriodFilterValue['periodType'];

type PeriodOption = {
  value: PeriodType;
  label: string;
};

@Component({
  selector: 'app-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ButtonComponent,
  ],
  templateUrl: './filter.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './filter.component.css'
})
export class FilterComponent {
  readonly store = inject(FilterStore);
  readonly dispatcher = inject(Dispatcher);
  readonly translateService = inject(TranslateService);

  readonly draftFilter = signal<Network>(this.cloneFilter(this.store.filter().network ?? this.emptyNetworkFilter()));
  readonly draftPeriodFilter = signal<PeriodFilterValue>(this.toPeriodDraft(this.store.filter().period));

  readonly draftActiveFilterCount = computed(() => this.countActiveFilters(this.draftFilter()));
  readonly showPeriodDatePickers = computed(() => this.draftPeriodFilter().periodType === 'free');

  readonly periodOptions: PeriodOption[] = [
    { value: 'free', label: 'app.label.free.selection' },
    { value: 'current_month', label: 'app.label.current.month' },
    { value: '3_months', label: 'app.label.three.consecutive.months' },
    { value: '6_months', label: 'app.label.six.consecutive.months' },
    { value: '12_months', label: 'app.label.twelve.consecutive.months' },
    { value: 'quarter_to_date', label: 'app.label.quarter.to.date' },
    { value: 'semester_to_date', label: 'app.label.semester.to.date' },
    { value: 'year_to_date', label: 'app.label.year.to.date' },
    { value: 'last_year_month', label: 'app.label.this.month.last.year' },
    { value: 'last_year_quarter', label: 'app.label.this.quarter' }
  ];

  private readonly filterLabels: Record<FilterKey, string> = {
    markets: 'app.label.markets',
    brands: 'app.label.brands',
    dealers: 'app.label.dealers',
    outlets: 'app.label.outlets',
    agents: 'app.label.locations'
  };

  readonly filterFields = (Object.keys(this.emptySupportedFilters()) as FilterKey[])
    .map((key) => ({ key, label: this.filterLabels[key] }));

  readonly searchTerms = signal<Record<FilterKey, string>>(this.createSearchTerms());
  readonly periodSearchTerm = signal('');
  readonly translatedPeriodOptions = computed(() =>
    this.periodOptions.map(option => ({
      value: option.value,
      label: this.translateService.instant(option.label)
    }))
  );
  private readonly mockOptions: Record<FilterKey, string[]> = {
    markets: [],
    brands: [],
    dealers: [],
    outlets: [],
    agents: []
  };

  readonly filteredAgentOptions = computed((): AgentOption[] => {
    const ids = (this.store.options().agents ?? []);
    const map = this.store.options().agentsMap ?? {};
    const search = this.getSearchTerm('agents').trim().toLowerCase();

    const list = ids.map(id => ({ id, name: map[id] ?? id }));
    const filtered = search
      ? list.filter(opt => opt.name.toLowerCase().includes(search) || String(opt.id).toLowerCase().includes(search))
      : list;

    // sort once
    return filtered.slice().sort((a, b) => a.name.localeCompare(b.name));
  });

  constructor() {
    effect(() => {
      if (this.store.isDrawerOpen()) {
        this.draftFilter.set(this.cloneFilter(this.store.filter().network ?? this.emptyNetworkFilter()));
        this.draftPeriodFilter.set(this.toPeriodDraft(this.store.filter().period));
        this.resetSearchTerms();
        this.periodSearchTerm.set('');
      }
    });
  }

  getSearchTerm(key: FilterKey): string {
    return this.searchTerms()[key];
  }

  getFilteredOptions(key: FilterKey): string[] {
    const searchTerm = this.getSearchTerm(key).trim().toLowerCase();

    const options = this.getAllOptions(key);
    return searchTerm
      ? options.filter((option) => option.toLowerCase().includes(searchTerm))
      : options;
  }

  getSelectedValues(key: FilterKey): string[] {
    return this.toStringArray(this.draftFilter()[key]);
  }

  getSelectedModel(key: FilterKey): unknown[] {
    return (this.draftFilter()[key] ?? []) as unknown[];
  }

  getSelectionSummary(key: FilterKey): string {
    const selected = this.getSelectedValues(key);
    if (!selected.length) {
      return `Select ${this.filterLabels[key].toLowerCase()}`;
    }

    if (key === 'agents') {
      const agentMap = new Map(this.getAgentOptions().map(a => [a.id, a.name]));
      const visibleSelections = selected.slice(0, 2).map(id => agentMap.get(id) ?? id).join(', ');
      return selected.length > 2
        ? `${visibleSelections} +${selected.length - 2}`
        : visibleSelections;
    }

    const visibleSelections = selected.slice(0, 2).join(', ');
    return selected.length > 2
      ? `${visibleSelections} +${selected.length - 2}`
      : visibleSelections;
  }

  getPeriodSelectionSummary(): string {
    const selected = this.translatedPeriodOptions().find(
      option => option.value === this.draftPeriodFilter().periodType
    );
    return selected?.label ??  'app.label.select.period';
  }


  getFilteredPeriodOptions(): PeriodOption[] {
    const term = this.periodSearchTerm().trim().toLowerCase();
    const options = this.translatedPeriodOptions();
    return term
      ? options.filter(option => option.label.toLowerCase().includes(term))
      : options;
  }

  // Always use the full agent id list from store.options().agents and resolve
  // display names via store.options().agentsMap. Never remove agents from this
  // list; only update selection state in draftFilter().agents.
  getAgentOptions(): AgentOption[] {
    const ids = (this.store.options().agents ?? []).slice();
    const map = this.store.options().agentsMap ?? {};
    return ids.map(id => ({ id, name: map[id] ?? id })).sort((a, b) => a.name.localeCompare(b.name));
  }

  getFilteredAgentOptions(): AgentOption[] {
    return this.filteredAgentOptions();
  }

  onSelectionChange(values: string[] | null, key: FilterKey): void {
    this.updateDraftFilter(key, values ?? []);
  }

  onSearchChange(term: string, key: FilterKey): void {
    this.searchTerms.update((current) => ({
      ...current,
      [key]: term
    }));
  }

  onPeriodSearchChange(term: string): void {
    this.periodSearchTerm.set(term);
  }

  onPeriodTypeChange(periodType: PeriodType): void {
    this.draftPeriodFilter.update((filter) => ({
      ...filter,
      periodType
    }));
  }

  onCurrentFromChange(date: Date | null): void {
    if (!date) return;
    this.draftPeriodFilter.update((filter) => ({
      ...filter,
      currentFrom: date,
      ...this.previousMonthOf(date)
    }));
  }

  onCurrentToChange(date: Date | null): void {
    if (!date) return;
    this.draftPeriodFilter.update((filter) => ({
      ...filter,
      currentTo: date,
      ...this.previousMonthOf(filter.currentFrom ?? date)
    }));
  }

  onPreviousFromChange(date: Date | null): void {
    if (!date) return;
    this.draftPeriodFilter.update((filter) => ({
      ...filter,
      previousFrom: date
    }));
  }

  onPreviousToChange(date: Date | null): void {
    if (!date) return;
    this.draftPeriodFilter.update((filter) => ({
      ...filter,
      previousTo: date
    }));
  }

  clearAll(): void {
    this.dispatcher.dispatch(clearAllFilters());

    const updated = this.store.filter();
    this.draftFilter.set(this.cloneFilter(updated.network ?? this.emptyNetworkFilter()));
    this.draftPeriodFilter.set(this.toPeriodDraft(updated.period));
    this.resetSearchTerms();
    this.periodSearchTerm.set('');
  }

  toggleDrawer(): void {
    this.dispatcher.dispatch(toggleFilterDrawer());
  }

  applyFilter(): void {
    const nextFilter: Filter = {
      network: {
        tenant: this.draftFilter().tenant,
        ...this.pickSupportedFilters(this.draftFilter())
      },
      period: this.toPeriod(this.draftPeriodFilter())
    };

    this.dispatcher.dispatch(applyFilter(nextFilter));
    this.dispatcher.dispatch(toggleFilterDrawer());
    this.resetSearchTerms();
    this.periodSearchTerm.set('');
  }

  private createSearchTerms(): Record<FilterKey, string> {
    return {
      markets: '',
      brands: '',
      dealers: '',
      outlets: '',
      agents: ''
    };
  }

  private emptySupportedFilters(): SupportedFilters {
    return {
      brands: [],
      dealers: [],
      outlets: [],
      agents: [],
    };
  }

  private pickSupportedFilters(filter: Network): SupportedFilters {
    return {
      markets: [...(filter.markets ?? [])],
      brands: [...(filter.brands ?? [])],
      dealers: [...(filter.dealers ?? [])],
      outlets: [...(filter.outlets ?? [])],
      agents: [...(filter.agents ?? [])]
    };
  }

  private resetSearchTerms(): void {
    this.searchTerms.set(this.createSearchTerms());
  }

  private updateDraftFilter(key: FilterKey, values: string[]): void {
    this.draftFilter.update((filter) => ({
      ...filter,
      [key]: values
    }));
  }

  private cloneFilter(filter: Network): Network {
    return {
      tenant: filter.tenant,
      ...this.pickSupportedFilters(filter)
    };
  }

  private countActiveFilters(filter: Network): number {
    return (Object.keys(this.emptySupportedFilters()) as FilterKey[])
      .reduce((count, key) => count + (filter[key]?.length ?? 0), 0);
  }

  private getAllOptions(key: FilterKey): string[] {
    return Array.from(new Set([
      ...this.toStringArray(this.store.options()[key]),
      ...this.mockOptions[key]
    ])).sort((left, right) => left.localeCompare(right));
  }

  private toStringArray(values: unknown[] | undefined): string[] {
    return (values ?? []).map((value) => String(value));
  }

  private emptyNetworkFilter(): Network {
    return {
      tenant: undefined,
      ...this.emptySupportedFilters()
    };
  }

  private defaultPeriodFilter(): PeriodFilterValue {
    const period = this.calculatePeriod('current_month');
    return {
      periodType: 'current_month',
      currentFrom: period.current.from,
      currentTo: period.current.to,
      previousFrom: period.previous?.from,
      previousTo: period.previous?.to
    };
  }

  private toPeriodDraft(period: Period | undefined): PeriodFilterValue {
    const fallback = this.defaultPeriodFilter();
    if (!period) {
      return fallback;
    }

    const datePreset = (period.datePreset ?? 'current_month') as PeriodPreset;
    return {
      periodType: datePreset,
      currentFrom: period.current?.from ? new Date(period.current.from) : fallback.currentFrom,
      currentTo: period.current?.to ? new Date(period.current.to) : fallback.currentTo,
      previousFrom: period.previous?.from ? new Date(period.previous.from) : fallback.previousFrom,
      previousTo: period.previous?.to ? new Date(period.previous.to) : fallback.previousTo
    };
  }

  private toPeriod(filter: PeriodFilterValue): Period {
    if (filter.periodType !== 'free') {
      return this.calculatePeriod(filter.periodType);
    }

    const fallback = this.calculatePeriod('current_month');
    return {
      current: {
        from: filter.currentFrom ?? fallback.current.from,
        to: filter.currentTo ?? fallback.current.to
      },
      previous: {
        from: filter.previousFrom ?? fallback.previous!.from,
        to: filter.previousTo ?? fallback.previous!.to
      },
      datePreset: 'free'
    };
  }

  private calculatePeriod(periodType: PeriodPreset): Period {
    const today = new Date();
    let currentFrom: Date;
    let currentTo: Date;
    let previousFrom: Date;
    let previousTo: Date;

    switch (periodType) {
      case 'current_month': {
        currentFrom = this.getStartOfMonth(today);
        currentTo = today;
        previousFrom = this.getStartOfMonth(this.addMonths(today, -1));
        previousTo = this.getEndOfMonth(this.addMonths(today, -1));
        break;
      }
      case '3_months': {
        currentTo = today;
        currentFrom = this.getStartOfMonth(this.addMonths(today, -2));
        previousTo = this.getEndOfMonth(this.addMonths(today, -3));
        previousFrom = this.getStartOfMonth(this.addMonths(today, -5));
        break;
      }
      case '6_months': {
        currentTo = today;
        currentFrom = this.getStartOfMonth(this.addMonths(today, -5));
        previousTo = this.getEndOfMonth(this.addMonths(today, -6));
        previousFrom = this.getStartOfMonth(this.addMonths(today, -11));
        break;
      }
      case '12_months': {
        currentTo = today;
        currentFrom = this.getStartOfMonth(this.addMonths(today, -11));
        previousTo = this.getEndOfMonth(this.addMonths(today, -12));
        previousFrom = this.getStartOfMonth(this.addMonths(today, -23));
        break;
      }
      case 'quarter_to_date': {
        currentFrom = this.getStartOfQuarter(today);
        currentTo = today;
        previousFrom = this.getStartOfQuarter(this.addMonths(today, -3));
        previousTo = this.getEndOfQuarter(this.addMonths(today, -3));
        break;
      }
      case 'semester_to_date': {
        currentFrom = today.getMonth() < 6 ? this.getStartOfYear(today) : new Date(today.getFullYear(), 6, 1);
        currentTo = today;
        previousFrom = today.getMonth() < 6 ? new Date(today.getFullYear() - 1, 6, 1) : this.getStartOfYear(today);
        previousTo = this.getEndOfMonth(this.addMonths(currentFrom, -1));
        break;
      }
      case 'year_to_date': {
        currentFrom = this.getStartOfYear(today);
        currentTo = today;
        previousFrom = this.getStartOfYear(this.addMonths(today, -12));
        previousTo = this.getEndOfYear(this.addMonths(today, -12));
        break;
      }
      case 'last_year_month': {
        const lastYearToday = this.addMonths(today, -12);
        currentFrom = this.getStartOfMonth(lastYearToday);
        currentTo = this.getEndOfMonth(lastYearToday);
        const twoYearsAgo = this.addMonths(today, -24);
        previousFrom = this.getStartOfMonth(twoYearsAgo);
        previousTo = this.getEndOfMonth(twoYearsAgo);
        break;
      }
      case 'last_year_quarter': {
        const lastYearToday = this.addMonths(today, -12);
        currentFrom = this.getStartOfQuarter(lastYearToday);
        currentTo = this.getEndOfQuarter(lastYearToday);
        const twoYearsAgo = this.addMonths(today, -24);
        previousFrom = this.getStartOfQuarter(twoYearsAgo);
        previousTo = this.getEndOfQuarter(twoYearsAgo);
        break;
      }
      case 'free':
      default: {
        currentFrom = this.getStartOfMonth(today);
        currentTo = today;
        previousFrom = this.getStartOfMonth(this.addMonths(today, -1));
        previousTo = this.getEndOfMonth(this.addMonths(today, -1));
        break;
      }
    }

    return {
      current: { from: currentFrom, to: currentTo },
      previous: { from: previousFrom, to: previousTo },
      datePreset: periodType
    };
  }

  // Free selection prefills the comparison period with the full month before the current start.
  private previousMonthOf(date: Date): Pick<PeriodFilterValue, 'previousFrom' | 'previousTo'> {
    const previousMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    return {
      previousFrom: this.getStartOfMonth(previousMonth),
      previousTo: this.getEndOfMonth(previousMonth)
    };
  }

  private getStartOfMonth(date: Date): Date {
    const d = new Date(date);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private getEndOfMonth(date: Date): Date {
    const d = new Date(date);
    d.setMonth(d.getMonth() + 1);
    d.setDate(0);
    d.setHours(23, 59, 59, 999);
    return d;
  }

  private getStartOfQuarter(date: Date): Date {
    const d = new Date(date);
    const quarter = Math.floor(d.getMonth() / 3);
    d.setMonth(quarter * 3, 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private getEndOfQuarter(date: Date): Date {
    const d = new Date(date);
    const quarter = Math.floor(d.getMonth() / 3);
    d.setMonth(quarter * 3 + 3, 0);
    d.setHours(23, 59, 59, 999);
    return d;
  }

  private getStartOfYear(date: Date): Date {
    const d = new Date(date);
    d.setMonth(0, 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private getEndOfYear(date: Date): Date {
    const d = new Date(date);
    d.setMonth(11, 31);
    d.setHours(23, 59, 59, 999);
    return d;
  }

  private addMonths(date: Date, months: number): Date {
    const d = new Date(date);
    d.setMonth(d.getMonth() + months);
    return d;
  }
}
